import axios, { type AxiosInstance } from 'axios';

const API_URL = (import.meta.env.VITE_API_URL as string | undefined) ?? 'http://localhost:5000/api';

const api: AxiosInstance = axios.create({ baseURL: API_URL });

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// ============================================================
// AUTH — translation layer entre o backend novo (/auth/login + /auth/me)
// e o shape que o frontend legacy espera ({ status, data: { user } }).
// ============================================================

export interface LegacyUser {
  id: string;
  name: string;
  email: string;
  role?: 'admin' | 'operator' | 'viewer';
  /** Compat: SAAS legado usava `accountType` em vez de `role` */
  accountType?: string;
  phone?: string;
  lastLogin?: string;
  [key: string]: unknown;
}

export interface LegacyAuthResponse {
  status: 'success' | 'error';
  token?: string;
  data?: { user: LegacyUser };
  message?: string;
}

export const authAPI = {
  async login(email: string, password: string): Promise<LegacyAuthResponse> {
    try {
      const r = await api.post<{ token: string; user: LegacyUser }>('/auth/login', { email, password });
      return {
        status: 'success',
        token: r.data.token,
        data: { user: { ...r.data.user, accountType: r.data.user.role } },
      };
    } catch (err) {
      const e = err as { response?: { data?: { message?: string } } };
      return {
        status: 'error',
        message: e.response?.data?.message ?? 'Erro no login',
      };
    }
  },

  async verify(): Promise<LegacyAuthResponse> {
    try {
      const r = await api.get<LegacyUser>('/auth/me');
      return {
        status: 'success',
        data: { user: { ...r.data, accountType: r.data.role } },
      };
    } catch (err) {
      const e = err as { response?: { data?: { message?: string } } };
      return {
        status: 'error',
        message: e.response?.data?.message ?? 'Sessão inválida',
      };
    }
  },

  async logout(): Promise<void> {
    try {
      await api.post('/auth/logout');
    } catch {
      // ignora — logout é local de qualquer jeito (deletar token)
    }
  },

  /**
   * @deprecated Cadastro público desabilitado no SAAS Farisx — admin cria via
   * CRUD /saas-users. Mantido pra não quebrar imports legacy.
   */
  async register(): Promise<LegacyAuthResponse> {
    return { status: 'error', message: 'Cadastro público desativado. Solicite ao admin.' };
  },

  async updateProfile(data: Partial<LegacyUser>): Promise<LegacyUser> {
    const me = await api.get<LegacyUser>('/auth/me');
    const r = await api.put<LegacyUser>(`/saas-users/${me.data.id}`, data);
    return r.data;
  },
};

// ============================================================
// USERS — aponta pro novo /saas-users (preserva shape legado)
// ============================================================

export const usersAPI = {
  async getUsers() {
    const r = await api.get('/saas-users');
    return r.data;
  },
  async createUser(userData: Partial<LegacyUser> & { password: string }) {
    const r = await api.post('/saas-users', userData);
    return r.data;
  },
  async updateUser(id: string, userData: Partial<LegacyUser>) {
    const r = await api.put(`/saas-users/${id}`, userData);
    return r.data;
  },
  async deleteUser(id: string) {
    const r = await api.delete(`/saas-users/${id}`);
    return r.data;
  },
};

// ============================================================
// ME / MODULES
// ============================================================

export interface ModuleInfo {
  id: 'prospect' | 'agendamento' | 'central';
  displayName: string;
  description: string;
}

export const meAPI = {
  async modules(): Promise<{ enabled: ModuleInfo[]; all: ModuleInfo[] }> {
    const r = await api.get('/me/modules');
    return r.data;
  },
};

// ============================================================
// CONFIG (runtime editable settings)
// ============================================================

export interface ConfigEntry {
  id: string;
  key: string;
  value: string;
  encrypted: boolean;
  updatedBy?: string;
  updatedAt: string;
}

export interface ConfigListResponse {
  data: ConfigEntry[];
  editableKeys: string[];
}

export const configAPI = {
  async list(): Promise<ConfigListResponse> {
    const r = await api.get<ConfigListResponse>('/config');
    return r.data;
  },
  async set(key: string, value: string): Promise<void> {
    await api.put(`/config/${encodeURIComponent(key)}`, { value });
  },
  async remove(key: string): Promise<void> {
    await api.delete(`/config/${encodeURIComponent(key)}`);
  },
  async uploadLogo(file: File): Promise<{ url: string }> {
    const fd = new FormData();
    fd.append('logo', file);
    const r = await api.post<{ url: string }>('/config/logo', fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return r.data;
  },
  async removeLogo(): Promise<void> {
    await api.delete('/config/logo');
  },
  /** Adiciona imagem ao carrossel da tela de login (Fase 28/30). */
  async uploadLoginBanner(file: File): Promise<{ url: string; images: string[] }> {
    const fd = new FormData();
    fd.append('image', file);
    const r = await api.post<{ url: string; images: string[] }>('/config/login-banner', fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return r.data;
  },
  /** Remove uma imagem do carrossel (url) ou todas (sem url). */
  async removeLoginBanner(url?: string): Promise<void> {
    await api.delete('/config/login-banner', { params: url ? { url } : undefined });
  },
};

// ============================================================
// BRANDING (público, sem auth) — identidade visual do cliente
// ============================================================

export interface BrandingLogin {
  notice: string | null;
  badge: string | null;
  title: string | null;
  body: string | null;
  ctaText: string | null;
  ctaUrl: string | null;
  /** Imagem de fundo do painel de anúncio (relativa ao backend) */
  imageUrl?: string | null;
  /** Carrossel de imagens do login (Fase 30) — relativas ao backend */
  images?: string[] | null;
}

export interface Branding {
  clientName: string;
  logoUrl: string | null;
  /** Textos da tela de login (Fase 28) — null em backends antigos */
  login?: BrandingLogin | null;
}

/**
 * Base URL do BACKEND (sem o /api final) — usado pra resolver caminhos
 * relativos retornados pela API (ex: /uploads/logo.png).
 */
export const API_ORIGIN = (() => {
  try {
    const u = new URL(API_URL);
    return `${u.protocol}//${u.host}`;
  } catch {
    return '';
  }
})();

export function resolveAssetUrl(p: string | null | undefined): string | null {
  if (!p) return null;
  if (/^https?:\/\//.test(p)) return p;
  return `${API_ORIGIN}${p.startsWith('/') ? p : `/${p}`}`;
}

export const brandingAPI = {
  async get(): Promise<Branding> {
    const r = await api.get<Branding>('/branding');
    return r.data;
  },
};

// ============================================================
// DASHBOARD (overview consolidado)
// ============================================================

export interface DashboardAgendamento {
  total: number;
  pendentes: number;
  confirmados: number;
  realizados: number;
  cancelados: number;
  proximos: Array<{ data: string; count: number }>;
}

export interface DashboardProspect {
  total: number;
  draft: number;
  pending: number;
  sent: number;
  confirmed: number;
  error: number;
  hoje: number;
}

export interface DashboardCentral {
  devicesAtivos: number;
  notificacoesHoje: number;
  notificacoesTotal: number;
}

export interface DashboardBilling {
  hasSubscription: boolean;
  status?: string;
  monthlyAmountCents?: number;
  currentPeriodEnd?: string;
  cancelAtPeriodEnd?: boolean;
  nextInvoice?: {
    amountDueCents: number;
    dueDate?: string;
    status: string;
  };
}

export interface DashboardOverview {
  client: string;
  modulesEnabled: string[];
  agendamento?: DashboardAgendamento;
  prospect?: DashboardProspect;
  central?: DashboardCentral;
  billing: DashboardBilling;
  generatedAt: string;
}

export const dashboardAPI = {
  async overview(): Promise<DashboardOverview> {
    const r = await api.get<DashboardOverview>('/dashboard/overview');
    return r.data;
  },
};

// ============================================================
// BILLING (Stripe)
// ============================================================

export type SubscriptionStatus =
  | 'trialing'
  | 'active'
  | 'past_due'
  | 'canceled'
  | 'unpaid'
  | 'incomplete'
  | 'incomplete_expired';

export interface SubscriptionItem {
  moduleId: 'prospect' | 'agendamento' | 'central';
  stripePriceId: string;
  quantity: number;
}

export interface Subscription {
  id: string;
  clientName: string;
  stripeCustomerId: string;
  stripeSubscriptionId: string;
  status: SubscriptionStatus;
  items: SubscriptionItem[];
  currentPeriodStart?: string;
  currentPeriodEnd?: string;
  cancelAtPeriodEnd: boolean;
  defaultPaymentMethod?: string;
  monthlyAmountCents?: number;
}

export type InvoiceStatus = 'draft' | 'open' | 'paid' | 'uncollectible' | 'void';

export interface BillingInvoice {
  id: string;
  stripeInvoiceId: string;
  number?: string;
  status: InvoiceStatus;
  amountDueCents: number;
  amountPaidCents: number;
  currency: string;
  dueDate?: string;
  paidAt?: string;
  hostedUrl?: string;
  invoicePdfUrl?: string;
  pixPaymentIntentId?: string;
  pixQrCode?: string;
  pixQrCodeExpiresAt?: string;
  createdAt: string;
}

export interface BillingInvoicesResponse {
  data: BillingInvoice[];
  pagination: { page: number; limit: number; total: number; pages: number };
}

export const billingAPI = {
  async subscription(): Promise<Subscription | null> {
    try {
      const r = await api.get<Subscription>('/billing/subscription');
      return r.data;
    } catch (err) {
      const e = err as { response?: { status?: number } };
      if (e.response?.status === 404) return null;
      throw err;
    }
  },
  async invoices(params: { page?: number; limit?: number; status?: InvoiceStatus } = {}) {
    const r = await api.get<BillingInvoicesResponse>('/billing/invoices', { params });
    return r.data;
  },
  async setupIntent(): Promise<{ clientSecret: string; intentId: string }> {
    const r = await api.post('/billing/setup-intent');
    return r.data;
  },
  async pix(
    invoiceId: string,
  ): Promise<{
    pixQrCode: string;
    paymentIntentId: string;
    expiresAt: string;
    cached: boolean;
  }> {
    const r = await api.post(`/billing/invoice/${invoiceId}/pix`);
    return r.data;
  },
};

// ============================================================
// AGENDAMENTO
// ============================================================

export type AgendamentoStatus =
  | 'pendente'
  | 'confirmado'
  | 'cancelado'
  | 'realizado'
  /** Encaixe do prospect aguardando aprovação (não consome vaga) */
  | 'pendente_encaixe'
  /** Encaixe negado sem agendamento anterior pra restaurar */
  | 'encaixe_rejeitado';
export type AgendamentoPeriodo = 'manha' | 'tarde';

export interface Agendamento {
  id: string;
  data: string; // ISO
  periodo: AgendamentoPeriodo;
  equipe: string;
  plano: string;
  contratoErpId: number;
  nomeCliente: string;
  /** CPF/CNPJ do cliente (só dígitos) — usado pra listar serviços no ERP */
  documentoCliente?: string;
  bairro?: string;
  cidade: string;
  status: AgendamentoStatus;
  origem: 'public' | 'admin' | 'prospect';
  protocoloUra?: string;
  /** OS do ERP vinculada na criação manual (Fase 24) */
  osErpId?: number;
  /** false = não debita o limite de vagas da cidade (OS de verificação etc) */
  consomeVaga?: boolean;
  /** Motivo da OS do ERP; vazio = instalação (página pública/prospect) */
  tipoServico?: string;
  observacoes?: string;
  /** Recado de logística do operador — exibido na visão "Por técnico" (Fase 31) */
  observacaoOperador?: string;
  createdAt: string;
  updatedAt: string;
}

/** Linha do mini-dashboard de ocupação por cidade da Agenda (Fase 31). */
export interface ResumoCidade {
  cidade: string;
  manha: number;
  tarde: number;
  /** null = cidade sem limite de vagas configurado */
  vagasManha: number | null;
  vagasTarde: number | null;
}

/** Contrato retornado pelo consultacliente do SGP (proxy admin/público). */
export interface SgpContrato {
  contratoId: number;
  razaoSocial: string;
  contratoStatusDisplay: string;
  servico_plano?: string;
  endereco_logradouro?: string;
  endereco_numero?: string;
  endereco_bairro?: string;
  endereco_cidade?: string;
  [k: string]: unknown;
}

export interface AgendamentoListResponse {
  data: Agendamento[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
    /** Contratos distintos no recorte inteiro (Fase 31) */
    totalContratos?: number;
  };
}

export interface AgendamentoFilters {
  /** Busca livre: cliente, contrato, bairro ou cidade (Fase 31) */
  q?: string;
  /** Status em CSV — multi-seleção (Fase 31) */
  status?: string;
  cidade?: string;
  contratoErpId?: number;
  dataInicio?: string;
  dataFim?: string;
  /** Tipos em CSV — valores exatos e/ou __instalacao__/__outro__ (Fase 30/31) */
  tipoServico?: string;
  /** Equipes/técnicos em CSV — multi-seleção (Fase 30) */
  equipe?: string;
  /** Período do dia (Fase 31) */
  periodo?: AgendamentoPeriodo;
  page?: number;
  limit?: number;
}

export interface SlotLimit {
  id: string;
  cidade: string;
  manha: number;
  tarde: number;
  active: boolean;
  createdAt: string;
  updatedAt: string;
}

/** Bloqueio de agenda (Fase 29) — dia indisponível numa cidade. */
export interface AgendamentoBloqueio {
  id: string;
  data: string; // ISO
  cidade: string;
  /** '' = todos os tipos de serviço */
  tipoServico: string;
  motivo?: string;
  createdAt: string;
}

export interface OrdemServico {
  id: number | string;
  cliente?: string;
  contrato?: number | string;
  motivo?: string;
  status?: string; // 'Aberta' | 'Em execução' | 'Encerrada' (vem como label do SGP)
  pop?: string;
  responsavel?: string;
  data_cadastro?: string;
  hora_cadastro?: string;
  data_agendamento?: string;
  hora_agendamento?: string;
  data_finalizacao?: string;
  hora_finalizacao?: string;
  [k: string]: unknown;
}

/** Chamado + OS do ERP (proxy /agendamento/chamados/:contratoErpId). */
export interface ChamadoERP {
  oc_protocolo: string;
  oc_tipo_descricao?: string;
  oc_data_cadastro?: string;
  oc_conteudo?: string;
  oc_status_descricao?: string;
  os_id: number | string;
  os_conteudo?: string;
  os_servicoprestado?: string;
  os_data_agendamento?: string;
  os_motivo_descricao?: string;
  os_status?: number | string;
  os_status_descricao?: string;
  os_tecnico_responsavel?: string;
  contrato_pop?: string;
}

export interface MotivoOS {
  id: number;
  descricao: string;
}

/** POP do ERP — `pop` vem como "CIDADE-UF". */
export interface PopERP {
  id: number;
  pop: string;
}

/** OS de /api/ura/ordemservico/list/ + marcação da Agenda local. */
export interface OrdemServicoERP {
  id: number;
  cliente?: string;
  contrato?: number;
  contrato_status?: string;
  contrato_status_id?: number;
  ocorrencia?: string;
  status?: string;
  status_id?: number;
  data_cadastro?: string;
  data_agendamento?: string | null;
  data_finalizacao?: string | null;
  tipo?: string;
  tipo_id?: number;
  motivo?: string;
  pop?: string;
  conteudo?: string;
  responsavel?: string;
  tecnicos_auxiliares?: string[];
  usuario?: string;
  usuario_finalizacao?: string;
  classificacoes?: { id: number; classificacao: string } | null;
  /** Agendamento ativo desta OS na Agenda do painel, se houver. */
  agendamentoLocal: {
    id: string;
    data: string;
    periodo: AgendamentoPeriodo;
    equipe: string;
  } | null;
}

export interface Tecnico {
  id: string;
  nome: string;
  equipe: string;
  telefone?: string;
  cidades: string[];
  active: boolean;
  observacoes?: string;
  /** Vínculo com o ERP SGP (preenchido pelo sync). */
  erpId?: number;
  username?: string;
  createdAt: string;
  updatedAt: string;
}

export const agendamentoAPI = {
  // Admin
  async list(filters: AgendamentoFilters = {}): Promise<AgendamentoListResponse> {
    const r = await api.get('/agendamento', { params: filters });
    return r.data;
  },
  async get(id: string): Promise<Agendamento> {
    const r = await api.get(`/agendamento/${id}`);
    return r.data;
  },
  /** Tipos de serviço distintos — popula o filtro da Agenda (Fase 30). */
  async tiposServico(): Promise<string[]> {
    const r = await api.get<string[]>('/agendamento/tipos-servico');
    return r.data;
  },
  /** Cidades distintas — popula o filtro da Agenda. */
  async cidades(): Promise<string[]> {
    const r = await api.get<string[]>('/agendamento/cidades');
    return r.data;
  },
  /** Mini-dashboard de ocupação por cidade no range de datas (Fase 31). */
  async resumoCidades(params: {
    dataInicio?: string;
    dataFim?: string;
    /** CSV — recorta as contagens Manhã/Tarde (vagas livres ficam reais) */
    tipoServico?: string;
    equipe?: string;
    /** CSV — limita as linhas da tabela às cidades selecionadas */
    cidade?: string;
  }): Promise<{
    dataInicio: string;
    dataFim: string;
    dias: number;
    cidades: ResumoCidade[];
  }> {
    const r = await api.get('/agendamento/resumo-cidades', { params });
    return r.data;
  },
  /**
   * Serviços atuais do contrato no ERP (Fase 32) — internet + adicionais.
   * O backend grava a lista de volta no `plano` do agendamento.
   */
  async planosContrato(id: string): Promise<{ planos: string[]; plano: string }> {
    const r = await api.get<{ planos: string[]; plano: string }>(
      `/agendamento/${id}/planos`,
    );
    return r.data;
  },
  /** Criação manual pelo operador (Fase 24 — modal "Novo agendamento"). */
  async create(data: {
    contratoErpId: number;
    nomeCliente: string;
    /** CPF/CNPJ usado na busca — o ERP lista os serviços por documento */
    documentoCliente?: string;
    cidade: string;
    data: string; // ISO
    periodo: AgendamentoPeriodo;
    bairro?: string;
    plano?: string;
    osErpId?: number;
    consomeVaga?: boolean;
    tipoServico?: string;
    protocoloUra?: string;
    observacoes?: string;
    equipe?: string;
  }): Promise<Agendamento> {
    const r = await api.post('/agendamento', data);
    return r.data;
  },
  /** Busca contratos (ativos E inativos) do CPF/CNPJ no ERP — rota admin. */
  async buscarContrato(cpfcnpj: string): Promise<{ contratos?: SgpContrato[]; msg?: string }> {
    const r = await api.post('/agendamento/buscar-contrato', { cpfcnpj });
    return r.data;
  },
  async update(id: string, data: Partial<Agendamento>): Promise<Agendamento> {
    const r = await api.patch(`/agendamento/${id}`, data);
    return r.data;
  },
  async remove(id: string): Promise<void> {
    await api.delete(`/agendamento/${id}`);
  },

  /** Dashboard do Monitor (Fase 26d/30) — cards + séries pros gráficos. */
  async dashboardStats(params: { periodo?: 'mes' | 'ano'; tipo?: string }): Promise<{
    periodo: 'mes' | 'ano';
    labels: string[];
    cards: { hoje: number; amanha: number; executadasHoje: number; pendentesHoje: number };
    porDia: { agendados: number[]; executados: number[] };
    porTecnico: Array<{
      tecnico: string;
      agendados: number[];
      executados: number[];
      totalAgendados: number;
      totalExecutados: number;
    }>;
    tipos: string[];
    /** Crescimento acumulado da base (instalações executadas) — Fase 30 */
    crescimentoBase?: { labels: string[]; base: number[] };
  }> {
    const r = await api.get('/agendamento/dashboard-stats', { params });
    return r.data;
  },

  // Bloqueios de agenda (Fase 29) — dia+cidade(+tipo) indisponível
  bloqueios: {
    async list(): Promise<AgendamentoBloqueio[]> {
      const r = await api.get<AgendamentoBloqueio[]>('/agendamento/bloqueios');
      return r.data;
    },
    async create(data: {
      data: string; // YYYY-MM-DD
      cidade: string;
      tipoServico?: string;
      motivo?: string;
    }): Promise<AgendamentoBloqueio> {
      const r = await api.post<AgendamentoBloqueio>('/agendamento/bloqueios', data);
      return r.data;
    },
    async remove(id: string): Promise<void> {
      await api.delete(`/agendamento/bloqueios/${id}`);
    },
  },

  // Slot limits (admin)
  slotLimits: {
    async list(): Promise<SlotLimit[]> {
      const r = await api.get('/agendamento/slot-limits');
      return r.data;
    },
    async create(data: { cidade: string; manha: number; tarde: number; active?: boolean }) {
      const r = await api.post('/agendamento/slot-limits', data);
      return r.data as SlotLimit;
    },
    async update(id: string, data: { manha?: number; tarde?: number; active?: boolean }) {
      const r = await api.put(`/agendamento/slot-limits/${id}`, data);
      return r.data as SlotLimit;
    },
    async remove(id: string) {
      await api.delete(`/agendamento/slot-limits/${id}`);
    },
  },

  // Monitoramento (SGP proxy)
  monitoramento: {
    async ordensServico(body: {
      dataInicio: string;
      dataFim: string;
      status?: Array<0 | 1 | 2>;
      pop?: number[];
      motivo?: number[];
    }): Promise<{ ordens: OrdemServico[]; count: number; statusBuscados: number[] }> {
      const r = await api.post('/agendamento/monitoramento/ordens-servico', body);
      return r.data;
    },
  },

  // Tecnicos (admin)
  // Chamados/OS do ERP (modal de detalhes — Fase 23)
  chamados: {
    async list(contratoErpId: number): Promise<ChamadoERP[]> {
      const r = await api.get<{ data: ChamadoERP[] }>(
        `/agendamento/chamados/${contratoErpId}`,
      );
      return r.data.data;
    },
    /** Reagenda + atribui técnico na OS do ERP. dataAgendamento: "AAAA-MM-DD HH:MM". */
    async updateOs(
      osId: number | string,
      data: { tecnicoResponsavel: string; dataAgendamento: string; servicoPrestado?: string },
    ): Promise<{ message: string }> {
      const r = await api.post(`/agendamento/chamados/${osId}/update`, data);
      return r.data;
    },
  },

  /** Fila de OS do ERP pra agendar (página Ordens de Serviço — Fase 35). */
  ordensServico: {
    async filtros(refresh = false): Promise<{ motivos: MotivoOS[]; pops: PopERP[] }> {
      const r = await api.get('/agendamento/ordens-servico/filtros', {
        params: refresh ? { refresh: '1' } : undefined,
      });
      return r.data;
    },
    async list(params: {
      dataInicio: string;
      dataFim: string;
      status?: 0 | 1 | 2;
      pop?: number[];
      motivo?: number[];
    }): Promise<{ total: number; truncado: boolean; ordens: OrdemServicoERP[] }> {
      const r = await api.get('/agendamento/ordens-servico', {
        params: {
          dataInicio: params.dataInicio,
          dataFim: params.dataFim,
          status: params.status ?? 0,
          pop: params.pop?.length ? params.pop.join(',') : undefined,
          motivo: params.motivo?.length ? params.motivo.join(',') : undefined,
        },
      });
      return r.data;
    },
    /** Reagenda a OS no ERP com o técnico e espelha na Agenda local. */
    async agendar(
      osId: number | string,
      data: {
        tecnicoId: string;
        data: string;
        periodo: AgendamentoPeriodo;
        hora?: string;
        contratoErpId: number;
        cliente: string;
        pop: string;
        motivo: string;
        observacaoOperador?: string;
      },
    ): Promise<{ message: string; agendamento: Agendamento }> {
      const r = await api.post(`/agendamento/ordens-servico/${osId}/agendar`, data);
      return r.data;
    },
  },

  tecnicos: {
    /** Importa técnicos do ERP SGP (upsert por erpId; novos vêm inativos). */
    async sync(): Promise<{ message: string; created: number; updated: number; totalErp: number }> {
      const r = await api.post('/agendamento/tecnicos/sync');
      return r.data;
    },
    async list(params: { equipe?: string; cidade?: string; active?: boolean } = {}): Promise<Tecnico[]> {
      const r = await api.get<Tecnico[]>('/agendamento/tecnicos', {
        params: {
          ...params,
          active: params.active === undefined ? undefined : String(params.active),
        },
      });
      return r.data;
    },
    async create(data: {
      nome: string;
      equipe?: string;
      telefone?: string;
      cidades?: string[];
      active?: boolean;
      observacoes?: string;
    }): Promise<Tecnico> {
      const r = await api.post<Tecnico>('/agendamento/tecnicos', data);
      return r.data;
    },
    async update(
      id: string,
      data: {
        nome?: string;
        equipe?: string;
        telefone?: string;
        cidades?: string[];
        active?: boolean;
        observacoes?: string;
      },
    ): Promise<Tecnico> {
      const r = await api.put<Tecnico>(`/agendamento/tecnicos/${id}`, data);
      return r.data;
    },
    async remove(id: string): Promise<void> {
      await api.delete(`/agendamento/tecnicos/${id}`);
    },
  },

  // Público (sem auth)
  public: {
    async agendamentoAtivo(cpfcnpj: string, contratoErpId: number): Promise<{
      agendamento: (Agendamento & { id: string }) | null;
      /** Encaixe aguardando aprovação do provedor (null se não houver) */
      encaixePendente?: { data: string; periodo: AgendamentoPeriodo } | null;
    }> {
      const r = await api.get('/agendamento/public/agendamento-ativo', {
        params: { cpfcnpj, contratoErpId },
      });
      return r.data;
    },
    async buscarContrato(cpfcnpj: string) {
      const r = await api.post('/agendamento/public/buscar-contrato', { cpfcnpj });
      return r.data;
    },
    async vagasNoMes(cidade: string, dataInicio: string, dataFim: string) {
      const r = await api.get('/agendamento/public/vagas', {
        params: { cidade, dataInicio, dataFim },
      });
      return r.data as {
        cidade: string;
        vagasPorData: Record<
          string,
          { manha: number | null; tarde: number | null; bloqueado?: boolean }
        >;
      };
    },
    async criar(data: {
      cpfcnpj: string;
      contratoErpId: number;
      data: string;
      periodo: AgendamentoPeriodo;
      nomeCliente: string;
      plano: string;
      cidade: string;
      bairro?: string;
      /** Pede vaga extra além do limite — fica pendente até aprovação */
      isSqueeze?: boolean;
    }): Promise<Agendamento> {
      const r = await api.post('/agendamento/public', data);
      return r.data;
    },
    async reagendar(id: string, data: {
      cpfcnpj: string;
      contratoErpId: number;
      data?: string;
      periodo?: AgendamentoPeriodo;
    }): Promise<Agendamento> {
      const r = await api.patch(`/agendamento/public/${id}`, data);
      return r.data;
    },
  },
};

// ============================================================
// BANNERS (Central do Cliente — CRUD admin, Fase 20)
// ============================================================

export interface BannerDetail {
  title?: string;
  body?: string;
  footer?: string;
}

export interface Banner {
  id: string;
  title: string;
  subtitle?: string;
  imageUrl: string;
  actionUrl?: string | null;
  backgroundColor: string;
  textColor: string;
  isActive: boolean;
  emVigencia: boolean;
  priority: number;
  startDate: string;
  endDate: string;
  cities: string[];
  detail: BannerDetail;
  views: number;
  clicks: number;
  createdAt: string;
}

export interface BannerViewer {
  cpfCnpj: string;
  viewedAt: string;
  clicked: boolean;
  clickedAt: string | null;
  cidade: string | null;
  bairro: string | null;
  deviceModel: string | null;
  platform: string | null;
}

export interface BannerStats {
  id: string;
  title: string;
  views: number;
  clicks: number;
  ctr: number;
  viewers: BannerViewer[];
}

export interface BannerInput {
  title: string;
  subtitle?: string;
  imageUrl: string;
  actionUrl?: string | null;
  isActive?: boolean;
  priority?: number;
  startDate?: string;
  endDate?: string;
  cities?: string[];
  detail?: BannerDetail;
}

export const bannersAPI = {
  async list(): Promise<Banner[]> {
    const r = await api.get<{ data: Banner[] }>('/banners/saas');
    return r.data.data;
  },
  async create(input: BannerInput): Promise<Banner> {
    const r = await api.post<{ data: Banner }>('/banners/saas', input);
    return r.data.data;
  },
  async update(id: string, input: Partial<BannerInput>): Promise<Banner> {
    const r = await api.put<{ data: Banner }>(`/banners/saas/${id}`, input);
    return r.data.data;
  },
  async toggle(id: string): Promise<{ id: string; isActive: boolean }> {
    const r = await api.put<{ data: { id: string; isActive: boolean } }>(
      `/banners/saas/${id}/toggle`,
    );
    return r.data.data;
  },
  async remove(id: string): Promise<void> {
    await api.delete(`/banners/saas/${id}`);
  },
  async stats(id: string): Promise<BannerStats> {
    const r = await api.get<{ data: BannerStats }>(`/banners/saas/${id}/stats`);
    return r.data.data;
  },
  async uploadImage(file: File): Promise<{ url: string }> {
    const fd = new FormData();
    fd.append('image', file);
    const r = await api.post<{ url: string }>('/banners/saas/upload', fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return r.data;
  },
};

// ============================================================
// PUSH (Central do Cliente — broadcasts admin)
// ============================================================

export type PushPlatform = 'ios' | 'android' | 'web';
export type PushTokenType = 'expo' | 'fcm' | 'apns' | 'unknown';
export type PushNotificationType =
  | 'fatura'
  | 'suporte'
  | 'promocao'
  | 'manutencao'
  | 'sistema'
  | 'custom';
export type PushNotificationStatus = 'pending' | 'sent' | 'delivered' | 'failed';

export interface PushDevice {
  id: string;
  userId: string;
  pushToken: string;
  platform: PushPlatform;
  tokenType: PushTokenType;
  deviceId: string;
  deviceModel?: string;
  osVersion?: string;
  appVersion?: string;
  cidade?: string | null;
  bairro?: string | null;
  lastActive: string;
  active: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface PushNotification {
  id: string;
  userId: string;
  title: string;
  body: string;
  data?: Record<string, unknown>;
  type: PushNotificationType;
  priority: 'normal' | 'high';
  tokens: string[];
  status: PushNotificationStatus;
  sentAt?: string;
  deliveredAt?: string;
  failedReason?: string;
  read: boolean;
  readAt?: string;
  createdAt: string;
  updatedAt: string;
}

export interface Pagination {
  page: number;
  limit: number;
  total: number;
  pages: number;
}

export interface PushSendPayload {
  userIds?: string[];
  filter?: { cidade?: string; bairro?: string };
  /** true = broadcast pra todos os devices ativos */
  broadcast?: boolean;
  payload: {
    title: string;
    body: string;
    data?: Record<string, unknown>;
    priority?: 'normal' | 'high';
  };
  type?: PushNotificationType;
}

export interface PushSendResult {
  targetUserCount: number;
  devicesCount: number;
  result: { sent: number; failed: number; invalidTokens: string[] };
  notificationsCreated: number;
}

// Scheduled push
export type ScheduledStatus = 'pending' | 'sent' | 'failed' | 'cancelled';

export interface ScheduledPush {
  id: string;
  scheduledFor: string;
  status: ScheduledStatus;
  type: PushNotificationType;
  target: {
    userIds?: string[];
    filter?: { cidade?: string; bairro?: string };
  };
  payload: {
    title: string;
    body: string;
    data?: Record<string, unknown>;
    priority: 'normal' | 'high';
  };
  createdBy?: string;
  sentAt?: string;
  resultSummary?: {
    targetUserCount: number;
    devicesCount: number;
    sent: number;
    failed: number;
  };
  failedReason?: string;
  createdAt: string;
  updatedAt: string;
}

export interface ScheduleBody {
  scheduledFor: string; // ISO
  type?: PushNotificationType;
  target: {
    userIds?: string[];
    filter?: { cidade?: string; bairro?: string };
    broadcast?: boolean;
  };
  payload: {
    title: string;
    body: string;
    data?: Record<string, unknown>;
    priority?: 'normal' | 'high';
  };
}

export const pushAPI = {
  async listDevices(params: {
    cidade?: string;
    bairro?: string;
    userId?: string;
    active?: boolean;
    page?: number;
    limit?: number;
  } = {}): Promise<{ data: PushDevice[]; pagination: Pagination }> {
    const r = await api.get('/push/devices', {
      params: {
        ...params,
        active: params.active === undefined ? undefined : String(params.active),
      },
    });
    return r.data;
  },
  async deactivateDevice(id: string): Promise<void> {
    await api.delete(`/push/devices/${id}`);
  },
  async send(body: PushSendPayload): Promise<PushSendResult> {
    const r = await api.post('/push/send', body);
    return r.data;
  },
  async listNotifications(params: {
    userId?: string;
    type?: PushNotificationType;
    status?: PushNotificationStatus;
    page?: number;
    limit?: number;
  } = {}): Promise<{ data: PushNotification[]; pagination: Pagination }> {
    const r = await api.get('/push/notifications', { params });
    return r.data;
  },

  // ── Scheduled ───────────────────────────────────────────
  async schedule(body: ScheduleBody): Promise<ScheduledPush> {
    const r = await api.post<ScheduledPush>('/push/schedule', body);
    return r.data;
  },
  async listScheduled(params: {
    status?: ScheduledStatus;
    page?: number;
    limit?: number;
  } = {}): Promise<{ data: ScheduledPush[]; pagination: Pagination }> {
    const r = await api.get('/push/schedule', { params });
    return r.data;
  },
  async cancelScheduled(id: string): Promise<void> {
    await api.delete(`/push/schedule/${id}`);
  },
};

export default api;
